import { ChangeEvent, ChangeEventHandler, MouseEvent, MouseEventHandler, useCallback, useLayoutEffect, useRef } from 'react'
import { faBarcodeRead } from '@fortawesome/pro-solid-svg-icons'
import { RenderPlatform } from "@fusion-advantage/backcompat"
import { L1SmallIconButton } from "../../form/l1/L1Button"
import L1TextField from '../../form/l1/L1TextField'
import { ValiditySeverity } from '@fusion-advantage/formlib'
import { ComplexElementSize } from '../../form/ComplexElementSize'
import { FusionColor } from '../../../theme/FusionStyle'
import { validitySeverityIndicatorProps } from '../Indicator/StatusIndicatorProps'

interface V2L2BarcodeScanFieldProps {
	// if the platform is native, we present a button to scan;
	// otherwise they get a static barcode icon
	platform: RenderPlatform
	size: ComplexElementSize
	value?: string
	onChange?: ChangeEventHandler<HTMLInputElement>
	onScanClick?: MouseEventHandler<HTMLButtonElement>
	severity?: ValiditySeverity
}

export const V2L2BarcodeScanField = ({platform, size, value, onChange, onScanClick, severity}: V2L2BarcodeScanFieldProps) => {
	const inputRef = useRef<HTMLInputElement>(null)
	const selectionRef = useRef<[number|null, number|null]|null>(null)
	// some very painful hoops to jump through to keep the cursor in place
	// while updating the user's typed characters to uppercase.
	// full analysis:
	// https://giacomocerquone.com/blog/keep-input-cursor-still/
	// dan was willing to accept lowercase characters, but the default
	// collation in sqlite is case sensitive, and i don't want to recreate
	// the table and update all the tests right now.
	useLayoutEffect(() => {
		if(selectionRef.current) {
			const [start, end] = selectionRef.current
			inputRef.current?.setSelectionRange(start, end)
		}
	}, [value])
	const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		if(inputRef.current) {
			selectionRef.current = [
				inputRef.current.selectionStart,
				inputRef.current.selectionEnd,
			]
		}
		if(onChange) {
			onChange(e)
		}
	}, [onChange])
	const handleScanClick = useCallback((e: MouseEvent<HTMLButtonElement>) => {
		selectionRef.current = null
		if(onScanClick) {
			onScanClick(e)
		}
	}, [onScanClick])
	let borderColor: FusionColor|undefined
	if(severity) {
		const props = validitySeverityIndicatorProps(severity)
		borderColor = props.color
	}
	return (
		<L1TextField
			inputRef={inputRef}
			size={size}
			suffixIcon={platform === RenderPlatform.Web ? faBarcodeRead : undefined}
			value={value}
			onChange={handleChange}
			tip={platform === RenderPlatform.Native ? (
				<L1SmallIconButton icon={faBarcodeRead} onClick={handleScanClick} />
			): null}
			borderColor={borderColor}
		/>
	)
}
